import { useMutation } from "react-query";

import { uploadToS3 } from "utils/aws/uploadToS3";

type UseUploadImageOptions = {
  onError?: (error: unknown) => void;
  onSuccess?: (data: any) => void;
  onSettled?: () => void;
};

type ImageFile = {
  filename: string;
  file: any;
  type: string;
};

/**
 * This hook is used to upload an image to AWS S3 and return the URL of the image.
 * @param {UseUploadImageOptions} options - Options for the hook
 * @returns {Object} - Object containing the uploadImage function, loading state, error state, and success state
 */
const useUploadImage = (options: UseUploadImageOptions) => {
  const { onError, onSuccess, onSettled } = options;

  const { mutate, isLoading, isError, error, isSuccess, ...rest } = useMutation(
    (image: ImageFile) =>
      uploadToS3({
        file: image.file,
        key: image.filename,
        params: {
          ACL: "public-read",
          ContentEncoding: "base64",
        },
      }),
    {
      onError: (error) => {
        console.error("Error uploading image:", error);
        onError?.(error);
      },
      onSuccess: (data: any) => {
        onSuccess?.(data);
      },
      onSettled: () => {
        onSettled?.();
      },
    }
  );

  return {
    uploadImage: mutate,
    isUploading: isLoading,
    isUploadError: isError,
    uploadError: error,
    isUploadSuccess: isSuccess,
    ...rest,
  };
};

export default useUploadImage;
